<script setup lang="ts">
import { computed, onBeforeMount, ref } from 'vue';
import urljoin from 'url-join';
import store from '@/store';
import { hasIntegrationAccess } from '@/api/user';
import SidebarMenuItem from './SidebarMenuItem.vue';
import CButton from '@/components/shared/CButton';
import CustomilyLogo from '@/assets/images/customily-logo.svg';
import CustomilyFullLogo from '@/assets/images/customily-full-logo.svg';
import { customily2StoreTypes } from '@/typings/storeType';
import { Roles, StoreType } from '@/typings/user';
import type { SidebarChildItem, SidebarMenuItem as SidebarMenuItemType, SidebarMenuSection } from './sidebarMenu';

const props = defineProps<{
    activeRoute?: string;
    hasAccountsToImpersonate?: boolean;
}>();

const emit = defineEmits<{
    (e: 'navigate', path: string): void;
    (e: 'logout'): void;
    (e: 'impersonate'): void;
    (e: 'impersonate-child'): void;
}>();

const isCollapsed = ref(false);
const toggleCollapsed = () => {
    isCollapsed.value = !isCollapsed.value;
    expandedItem.value = null;
};

const expandedItem = ref<string | null>(null);
const onToggle = (name: string) => {
    if (isCollapsed.value) {
        isCollapsed.value = false;
        expandedItem.value = name;
        return;
    }
    expandedItem.value = expandedItem.value === name ? null : name;
};

const onNavigate = (item: SidebarChildItem) => {
    if (item.route) {
        emit('navigate', item.route);
        return;
    }

    item.action?.();
};

const getInitials = (name?: string) => name?.toUpperCase().slice(0, 2);
const userInfo = computed(() => {
    const user = store.state.user;
    const name = user?.Name || user?.Username;
    const impersonatedFrom = user?.UserImpersonatedFrom;
    const impersonatedFromName = impersonatedFrom?.Name || impersonatedFrom?.Username;
    return {
        isImpersonated: impersonatedFrom != undefined,
        fullName: impersonatedFromName || name,
        username: user?.Username,
        initials: impersonatedFrom ? getInitials(impersonatedFromName) : getInitials(name),
        impersonatedInitials: getInitials(name)
    };
});

const isImpersonateEnabled = computed(() => {
    const user = store.state.user;
    return !user?.ImpersonatedFromChildUser && (userRoles.value.has(Roles.ADMIN) || user?.ImpersonatedFromAdminUser);
});

const isSwitchTeamEnabled = computed(() => {
    const user = store.state.user;
    return (
        props.hasAccountsToImpersonate &&
        (user?.ImpersonatedFromParentUser ||
            userRoles.value.has(Roles.PARENT) ||
            user?.ImpersonatedFromChildUser ||
            userRoles.value.has(Roles.IMPERSONATE_PARENT))
    );
});

const userRoles = computed(() => {
    const user = store.state.user;
    const roles = user?.AccessRoles?.Serialized.split(',') || [];
    return new Set(roles);
});

const userAccess = computed(() => {
    const user = store.state.user;
    const access = {
        customily2: false,
        store: false,
        orders: false,
        billing: false,
        podBilling: false
    };
    let hasBilling = false;
    for (const { Type: type, HasBilling, HasPODBilling, Disabled } of user?.Stores || []) {
        access.customily2 ||= customily2StoreTypes.includes(type) && !Disabled;
        access.orders ||=
            [StoreType.Amazon, StoreType.Shopify, StoreType.WooCommerce, StoreType.Etsy].includes(type) ||
            (type === StoreType.Custom && userRoles.value.has(Roles.CUSTOM_STORE));
        access.store = access.customily2 || access.orders || type === StoreType.Amazon;
        access.podBilling ||= HasPODBilling;

        hasBilling ||= HasBilling;
        if (HasBilling || (type === StoreType.Shopify && !HasBilling)) {
            access.billing = true;
        }
    }
    access.podBilling = !hasBilling && access.podBilling;
    return access;
});

const sections = computed(() => {
    const user = store.state.user;
    const sections: SidebarMenuSection[] = [
        {
            hidden: userRoles.value.has(Roles.COLLECTIVE_RETAIL),
            items: [
                {
                    name: 'Home',
                    icon: ['far', 'house'],
                    route: userAccess.value.customily2 ? '/store/podSelection/' : '/store',
                    hidden: !userAccess.value.store && !userAccess.value.customily2
                }
            ]
        },
        {
            name: 'Create',
            hidden: userRoles.value.has(Roles.COLLECTIVE_RETAIL),
            items: [
                {
                    name: 'Libraries',
                    icon: ['far', 'swatchbook'],
                    route: '/libraries',
                    hidden: !user?.ShowLibraries
                },
                {
                    name: 'Designs',
                    icon: ['far', 'brush'],
                    route: '/designs',
                    hidden: !userAccess.value.customily2
                },
                {
                    name: 'Product Bases',
                    icon: ['far', 'shirt'],
                    route: '/myProductBases',
                    hidden: !userAccess.value.customily2
                },
                {
                    name: 'POD Product Bases',
                    icon: ['far', 'shirt'],
                    route: '/pod/productBases',
                    hidden: !userRoles.value.has(Roles.EXTERNAL_POD_USER)
                },
                {
                    name: 'Templates',
                    icon: ['far', 'pen-paintbrush'],
                    route: '/home',
                    hidden: userRoles.value.size == 1 && userRoles.value.has(Roles.AFFILIATE)
                },
                {
                    name: 'Option Sets',
                    icon: ['far', 'list-tree'],
                    route: '/store/sets/',
                    hidden: !userAccess.value.store
                }
            ]
        },
        {
            name: 'Manage',
            items: [
                {
                    name: 'Products',
                    icon: ['far', 'tags'],
                    route: '/store/products/',
                    hidden: !userAccess.value.store
                },
                {
                    name: 'Orders',
                    icon: ['far', 'receipt'],
                    route: '/store/orders/',
                    hidden: !userAccess.value.orders
                },
                {
                    name: 'Store Settings',
                    icon: ['far', 'store'],
                    route: '/store/settings/',
                    hidden: !userAccess.value.store
                },
                {
                    name: 'Manage Options',
                    icon: ['far', 'list-tree'],
                    route: '/options',
                    hidden: !user?.ShowCustomilyOptions || userRoles.value.has(Roles.COLLECTIVE_RETAIL)
                },
                {
                    name: 'Affiliates',
                    icon: ['far', 'circle-dollar'],
                    route: '/affiliates',
                    hidden: !userRoles.value.has(Roles.AFFILIATE)
                },
                {
                    name: 'Admin',
                    icon: ['far', 'screwdriver-wrench'],
                    hidden:
                        user?.ImpersonatedFromChildUser ||
                        (!userRoles.value.has(Roles.HANGFIRE) && !userRoles.value.has(Roles.ADMIN)),
                    children: [
                        {
                            name: 'Hangfire Jobs',
                            hidden: !userRoles.value.has(Roles.HANGFIRE),
                            href: '/hangfire'
                        },
                        {
                            name: 'Bullmq Jobs',
                            hidden: !userRoles.value.has(Roles.HANGFIRE),
                            href: urljoin(process.env.VUE_APP_SHOPIFY_APP_URL, '/admin/bullmq')
                        },
                        {
                            name: 'Admin',
                            hidden: !(userRoles.value.has(Roles.ADMIN) && userRoles.value.has(Roles.ADMIN_UI)),
                            route: '/admin'
                        }
                    ]
                }
            ]
        }
    ];
    return sections
        .map((section) => ({
            ...section,
            items: section.items?.filter((item) => !item.hidden)
        }))
        .filter((section) => section.items?.length && !section.hidden);
});

const accountSection = computed<SidebarMenuItemType[]>(() => {
    const user = store.state.user;
    return [
        {
            name: 'Account',
            icon: ['far', 'circle-user'],
            children: [
                {
                    name: 'Settings',
                    route: '/account'
                },
                {
                    name: 'Integrations',
                    hidden:
                        userRoles.value.has(Roles.COLLECTIVE_RETAIL) ||
                        (user?.ImpersonatedFromChildUser && !canManageIntegrations.value) ||
                        (userRoles.value.has(Roles.AFFILIATE) && !user?.Stores.some((s) => !s.Disabled)),
                    route: '/account/integrations'
                },
                {
                    name: 'Billing',
                    hidden:
                        userRoles.value.has(Roles.COLLECTIVE_RETAIL) ||
                        user?.ImpersonatedFromChildUser ||
                        !userAccess.value.billing,
                    route: '/account/billing'
                },
                {
                    name: 'POD Billing',
                    hidden:
                        userRoles.value.has(Roles.COLLECTIVE_RETAIL) ||
                        user?.ImpersonatedFromChildUser ||
                        !userAccess.value.podBilling,
                    route: '/account/podBilling'
                },
                {
                    name: 'Team',
                    hidden: userRoles.value.has(Roles.COLLECTIVE_RETAIL),
                    route: '/account/team'
                }
            ]
        },
        {
            name: 'Help Center',
            icon: ['far', 'headset'],
            href: 'https://help.customily.com/hc/en-us'
        },
        {
            name: 'Log Out',
            icon: ['far', 'arrow-right-from-bracket'],
            action: () => emit('logout')
        }
    ];
});

const canManageIntegrations = ref(true);
const loadPermissionsData = async () => {
    const response = await hasIntegrationAccess();
    canManageIntegrations.value = response;
};

onBeforeMount(() => {
    loadPermissionsData();
});
</script>

<template>
    <div class="sidebar-wrapper" :class="{ collapsed: isCollapsed }">
        <div class="header">
            <img :src="isCollapsed ? CustomilyLogo : CustomilyFullLogo" alt="customily-logo" />
        </div>
        <button
            type="button"
            class="collapse-toggle"
            :class="{ collapsed: isCollapsed }"
            :aria-label="isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'"
            @click="toggleCollapsed"
        >
            <font-awesome-icon :icon="['far', 'left-to-line']" />
        </button>
        <div class="content">
            <template v-for="(section, idx) in sections">
                <section v-if="!section.hidden" :key="`section-${section.name}-${idx}`" class="nav-section">
                    <div v-if="section.name?.length" class="section-title">{{ section.name }}</div>
                    <sidebar-menu-item
                        v-for="item in section.items"
                        :key="item.name"
                        :item="item"
                        :collapsed="isCollapsed"
                        :expanded="expandedItem === item.name"
                        :active-route="activeRoute"
                        @toggle="onToggle"
                        @navigate="onNavigate"
                    />
                </section>
            </template>
            <div class="nav-section mt-auto">
                <div
                    class="impersonate-container"
                    :class="{ 'bg-purple-lightest border-color-purple-light': userInfo.isImpersonated }"
                >
                    <div class="avatar">
                        {{ userInfo.initials }}
                        <div v-if="userInfo.isImpersonated" class="impersonate-avatar">
                            {{ userInfo.impersonatedInitials }}
                        </div>
                    </div>
                    <template v-if="!isCollapsed">
                        <div class="user-info mr-2">
                            <span class="font-weight-bold">{{ userInfo.fullName }}</span>
                            <span class="text-truncate">
                                {{ userInfo.username }}
                            </span>
                        </div>
                        <c-button
                            v-if="isImpersonateEnabled"
                            v-tippy="{ content: 'Impersonate', boundary: 'window' }"
                            class="ml-auto color-purple"
                            size="icon"
                            :icon="['fas', 'face-disguise']"
                            :disabled="!hasAccountsToImpersonate"
                            @click="emit('impersonate')"
                        />
                        <c-button
                            v-else-if="isSwitchTeamEnabled"
                            v-tippy="{ content: 'Switch Team', boundary: 'window' }"
                            size="icon"
                            :icon="['fas', 'arrow-left-arrow-right']"
                            @click="emit('impersonate-child')"
                        />
                    </template>
                </div>
                <sidebar-menu-item
                    v-for="item in accountSection"
                    :key="item.name"
                    :item="item"
                    :active-route="activeRoute"
                    :collapsed="isCollapsed"
                    :expanded="expandedItem === item.name"
                    @toggle="onToggle"
                    @navigate="onNavigate"
                />
            </div>
        </div>
    </div>
</template>

<style scoped lang="scss">
@import 'styles/utilities/functions';

.sidebar-wrapper {
    position: relative;
    display: flex;
    flex-direction: column;
    width: 252px;
    box-shadow: 0 2px 4px 0 rgba(0, 0, 0, 0.08);
    border-right: 1px solid get-color('gray-light');
    &:hover {
        .collapse-toggle {
            display: block;
        }
    }
    .collapse-toggle {
        display: none;
        position: absolute;
        top: 106px;
        right: -12px;
        z-index: 1;
        width: 24px;
        height: 24px;
        font-size: 0.75rem;
        color: get-color('white');
        background: get-color('purple-dark');
        border: none;
        border-radius: 50%;
        box-shadow: 0 1px 4px rgba(0, 0, 0, 0.25);
        svg {
            transition: transform 0.2s ease;
        }
        &.collapsed svg {
            transform: rotate(180deg);
        }
    }
    &.collapsed {
        width: 78px;
        .header {
            justify-content: center;
        }
        .content {
            .nav-section {
                justify-content: center;
                .section-title {
                    padding-right: 0;
                    padding-left: 0;
                    text-align: center;
                }
            }
        }
    }
    .content {
        display: flex;
        flex-direction: column;
        flex: 1;
        padding: 0.5rem 0.5rem 1rem;
        overflow: auto;
        .nav-section {
            display: flex;
            flex-direction: column;
            gap: 0.25rem;
            margin: 0.25rem 0;
            .section-title {
                font-weight: 700;
                font-size: 0.625rem;
                padding: 0.5rem 1rem 0;
                text-transform: uppercase;
                border-top: 1px solid get-color('gray');
                color: get-color('gray');
            }
        }
        .impersonate-container {
            display: flex;
            align-items: center;
            gap: 0.5rem;
            padding: 0.5rem;
            border: 1px solid get-color('gray-light');
            border-radius: 0.5rem;
            .user-info {
                display: flex;
                flex-direction: column;
                flex: 1;
                font-size: 0.875rem;
                overflow: hidden;
            }
            .avatar {
                position: relative;
                display: flex;
                justify-content: center;
                align-items: center;
                background-color: get-color('purple');
                color: white;
                font-weight: bold;
                font-size: 1.3rem;
                text-transform: uppercase;
                width: 42px;
                height: 42px;
                border: 1px solid get-color('gray-light');
                border-radius: 0.5rem;
                .impersonate-avatar {
                    position: absolute;
                    font-size: 0.625rem;
                    top: 23px;
                    left: 25px;
                    width: 20px;
                    height: 20px;
                    background-color: get-color('warning');
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    border-radius: 50%;
                }
            }
        }
    }
    .header {
        display: flex;
        align-items: center;
        color: get-color('white');
        background-color: get-color('purple');
        padding: 0 1rem;
        height: 35px;
        flex-shrink: 0;
    }
}
</style>
